import React, { useState, useEffect } from 'react';

interface ComplianceStatus {
  framework: string;
  status: 'compliant' | 'at_risk' | 'non_compliant';
  lastAudit: string;
  nextAudit: string;
  score: number;
}

interface RiskScore {
  category: string;
  score: number;
  trend: 'up' | 'down' | 'stable';
  issues: number;
}

interface AuditHistory {
  id: string;
  date: string;
  framework: string;
  result: 'passed' | 'failed' | 'partial';
  findings: number;
}

const ComplianceForgeDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);

  const complianceStatuses: ComplianceStatus[] = [
    { framework: 'GDPR', status: 'compliant', lastAudit: '2025-10-15', nextAudit: '2026-01-15', score: 94 },
    { framework: 'CCPA', status: 'compliant', lastAudit: '2025-10-20', nextAudit: '2026-01-20', score: 91 },
    { framework: 'SOC 2', status: 'at_risk', lastAudit: '2025-09-01', nextAudit: '2025-12-01', score: 78 },
    { framework: 'ISO 27001', status: 'compliant', lastAudit: '2025-08-15', nextAudit: '2026-02-15', score: 89 },
    { framework: 'HIPAA', status: 'non_compliant', lastAudit: '2025-07-01', nextAudit: '2025-11-30', score: 62 }
  ];

  const riskScores: RiskScore[] = [
    { category: 'Data Security', score: 85, trend: 'up', issues: 3 },
    { category: 'Access Control', score: 72, trend: 'down', issues: 8 },
    { category: 'Data Privacy', score: 91, trend: 'stable', issues: 1 },
    { category: 'Incident Response', score: 68, trend: 'up', issues: 5 },
    { category: 'Vendor Management', score: 79, trend: 'stable', issues: 4 }
  ];

  const auditHistory: AuditHistory[] = [
    { id: 'AUD-001', date: '2025-10-20', framework: 'CCPA', result: 'passed', findings: 2 },
    { id: 'AUD-002', date: '2025-10-15', framework: 'GDPR', result: 'passed', findings: 5 },
    { id: 'AUD-003', date: '2025-09-01', framework: 'SOC 2', result: 'partial', findings: 12 },
    { id: 'AUD-004', date: '2025-08-15', framework: 'ISO 27001', result: 'passed', findings: 7 },
    { id: 'AUD-005', date: '2025-07-01', framework: 'HIPAA', result: 'failed', findings: 23 }
  ];

  useEffect(() => {
    setTimeout(() => setLoading(false), 500);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'bg-emerald-100 text-emerald-800';
      case 'at_risk': return 'bg-yellow-100 text-yellow-800';
      case 'non_compliant': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getResultColor = (result: string) => {
    switch (result) {
      case 'passed': return 'text-emerald-600';
      case 'partial': return 'text-yellow-600';
      case 'failed': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <span className="text-emerald-600">^</span>;
      case 'down': return <span className="text-red-600">v</span>;
      default: return <span className="text-gray-600">-</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading compliance data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">ComplianceForge Dashboard</h1>
          <p className="text-gray-600">Real-time compliance monitoring and risk assessment</p>
        </div>

        {/* Overall Score */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-2">Overall Compliance</div>
            <div className="text-3xl font-bold text-emerald-600">82.8%</div>
            <div className="text-xs text-gray-500 mt-1">Across 5 frameworks</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-2">Active Risks</div>
            <div className="text-3xl font-bold text-yellow-600">21</div>
            <div className="text-xs text-gray-500 mt-1">3 critical, 8 high</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-2">DSR Requests</div>
            <div className="text-3xl font-bold text-indigo-600">7</div>
            <div className="text-xs text-gray-500 mt-1">Pending this month</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-2">Next Audit</div>
            <div className="text-3xl font-bold text-gray-900">12d</div>
            <div className="text-xs text-gray-500 mt-1">HIPAA - Nov 30</div>
          </div>
        </div>

        {/* Compliance Status */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Compliance Status</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b">
                  <th className="pb-3">Framework</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3">Score</th>
                  <th className="pb-3">Last Audit</th>
                  <th className="pb-3">Next Audit</th>
                </tr>
              </thead>
              <tbody>
                {complianceStatuses.map((item) => (
                  <tr key={item.framework} className="border-b last:border-0">
                    <td className="py-4 font-medium text-gray-900">{item.framework}</td>
                    <td className="py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              item.score >= 80 ? 'bg-emerald-500' : item.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${item.score}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">{item.score}%</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-600">{item.lastAudit}</td>
                    <td className="py-4 text-sm text-gray-600">{item.nextAudit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Risk Scores */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h2>
            <div className="space-y-4">
              {riskScores.map((risk) => (
                <div key={risk.category} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{risk.category}</span>
                      <div className="flex items-center gap-2">
                        {getTrendIcon(risk.trend)}
                        <span className="text-sm text-gray-600">{risk.score}</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          risk.score >= 80 ? 'bg-emerald-500' : risk.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${risk.score}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{risk.issues} open issues</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Audit History */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Audits</h2>
            <div className="space-y-4">
              {auditHistory.map((audit) => (
                <div key={audit.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{audit.framework}</span>
                      <span className={`text-sm font-medium ${getResultColor(audit.result)}`}>
                        {audit.result}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">{audit.date}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">{audit.findings} findings</div>
                    <div className="text-xs text-gray-500">{audit.id}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Privacy Agents Status */}
        <div className="bg-white rounded-xl shadow-sm p-6 mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Privacy Agents Status</h2>
          <div className="grid grid-cols-3 md:grid-cols-9 gap-4">
            {[
              'ConsentManagement',
              'DataGovernance',
              'DataMapping',
              'DataSubjectRights',
              'AITransfer',
              'GovtDataProcessing',
              'PrivacyImpact',
              'PrivacyTraining',
              'RetentionPolicy'
            ].map((agent) => (
              <div key={agent} className="text-center">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                </div>
                <div className="text-xs text-gray-600 truncate">{agent}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceForgeDashboard;
